/**
 * AgenticConfigEditor.tsx — the tab-strip panel that replaces the legacy
 * grounding editor when the loaded row carries `agent_config_json`
 * (ADR-084 D6, design doc `20260922_GARRETT_mari-agentic-config-editor.md`).
 * Rendered by `SkillVersionEditForm.tsx` in place of the grounding/
 * product-facts/classifiers sections — that file's own version-input,
 * created banner and topology `<details>` stay unchanged around it.
 *
 * Tabs: Persona · Boundaries · Cadence · Scripts (placeholder, S3b) · Facts ·
 * Eligibility & checklist (read-only). Persona/Boundaries/Scripts/Facts are
 * Admin-only, hidden client-side (`useIsAdmin`) — the server is the real
 * gate (ADR-084 D3). Cadence and the read-only panel always show.
 *
 * Save sends the FULL `AgentConfigDoc` as `agent_config_json`, alongside the
 * base row's `prompt_template_json` sent back unchanged (capital-service's
 * `CreateSkillVersionRequestBody.prompt_template_json` has no default — see
 * `api/types.ts`), through `apiClient.createSkillVersion`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import {
  asAgentConfigDoc,
  isLegacyShapeAgentConfig,
  type AgentConfigDoc,
  type BoundaryRule,
  type CreateSkillVersionResponse,
  type DocumentChecklistConfig,
  type FactDef,
  type Persona,
  type Script,
  type SkillVersionDetail,
  type SkillVersionFieldError,
  type VehicleEligibilityConfig,
} from '../../api/types';
import type { SkillVersionDetailParams } from './useSkillVersionDetail';
import { ActivateVersionControl } from './ActivateVersionControl';
import { PersonaTab } from './PersonaTab';
import { BoundariesTab } from './BoundariesTab';
import { CadenceTab } from './CadenceTab';
import { ScriptsTab } from './ScriptsTab';
import { FactsTab } from './FactsTab';
import { EligibilityChecklistPanel } from './EligibilityChecklistPanel';
import { Button } from '../../components/Button';
import { onTabListKeyDown } from '../../components/TabStrip';
import floating from '../../lib/floating';

// ADR-085 D6: eligibility/checklist edits go through the same Admin gate as
// the other editable tabs (ADR-084 D3) — hidden client-side for a non-Admin,
// same as Persona/Boundaries/Scripts/Facts, not shown read-only.
const ADMIN_ONLY_TABS = ['persona', 'boundaries', 'scripts', 'facts', 'eligibility'] as const;
const ALL_TABS = ['persona', 'boundaries', 'cadence', 'scripts', 'facts', 'eligibility'] as const;
type TabId = (typeof ALL_TABS)[number];

export interface AgenticConfigEditorProps {
  apiClient: ApiClient;
  base: SkillVersionDetailParams;
  detail: SkillVersionDetail;
  isAdmin: boolean;
  newConfigVersion: string;
  createDisabled: boolean;
  onCreateStart: () => void;
  onCreated: (response: CreateSkillVersionResponse) => void;
  onCreateError: (kind: 'unavailable' | 'unauthorized') => void;
  created: CreateSkillVersionResponse | null;
  onActivated?: () => void;
}

export const AgenticConfigEditor = ({
  apiClient,
  base,
  detail,
  isAdmin,
  newConfigVersion,
  createDisabled: createBusy,
  onCreateStart,
  onCreated,
  onCreateError,
  created,
  onActivated = undefined,
}: AgenticConfigEditorProps) => {
  const { t } = useTranslation();
  const isLegacy = isLegacyShapeAgentConfig(detail.agent_config_json ?? {});
  const createDisabled = createBusy || isLegacy;
  const [doc, setDoc] = useState<AgentConfigDoc>(() =>
    asAgentConfigDoc(detail.agent_config_json ?? {}),
  );
  const [fieldErrors, setFieldErrors] = useState<SkillVersionFieldError[]>([]);
  const visibleTabs = ALL_TABS.filter(
    (tab) => isAdmin || !ADMIN_ONLY_TABS.includes(tab as (typeof ADMIN_ONLY_TABS)[number]),
  );
  const [activeTab, setActiveTab] = useState<TabId>(visibleTabs[0]!);

  const setPersona = (next: Persona) =>
    setDoc((prev) => ({ ...prev, definition: { ...prev.definition, persona: next } }));
  const setBoundaryRules = (next: BoundaryRule[]) =>
    setDoc((prev) => ({ ...prev, boundaries: { ...prev.boundaries, rules: next } }));
  const setCadence = (next: string) => setDoc((prev) => ({ ...prev, cadence_instructions: next }));
  const setFacts = (next: FactDef[]) => setDoc((prev) => ({ ...prev, facts: next }));
  const setScripts = (next: Script[]) => setDoc((prev) => ({ ...prev, scripts: next }));
  const setEligibility = (next: VehicleEligibilityConfig) =>
    setDoc((prev) => ({ ...prev, eligibility: next }));
  const setChecklist = (next: DocumentChecklistConfig) =>
    setDoc((prev) => ({ ...prev, document_checklist: next }));

  const handleCreate = async () => {
    onCreateStart();
    setFieldErrors([]);
    try {
      const response = await apiClient.createSkillVersion(base.skillName, {
        product_code: base.productCode,
        base_config_version: base.configVersion,
        new_config_version: newConfigVersion.trim(),
        prompt_template_json: detail.prompt_template_json,
        agent_config_json: doc,
      });
      onCreated(response);
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        const { body } = error;
        let list: unknown[] = [];
        if (Array.isArray(body)) {
          list = body;
        } else if (
          body != null &&
          typeof body === 'object' &&
          Array.isArray((body as { detail?: unknown }).detail)
        ) {
          list = (body as { detail: unknown[] }).detail;
        }
        setFieldErrors(
          list.filter(
            (item): item is SkillVersionFieldError =>
              item != null &&
              typeof item === 'object' &&
              typeof (item as { field_path?: unknown }).field_path === 'string' &&
              typeof (item as { reason?: unknown }).reason === 'string',
          ),
        );
      } else if (error instanceof UnauthorizedError) {
        onCreateError('unauthorized');
      } else {
        onCreateError('unavailable');
      }
    }
  };

  const tabLabel: Record<TabId, string> = {
    persona: t('config.editAgentic.tabs.persona'),
    boundaries: t('config.editAgentic.tabs.boundaries'),
    cadence: t('config.editAgentic.tabs.cadence'),
    scripts: t('config.editAgentic.tabs.scripts'),
    facts: t('config.editAgentic.tabs.facts'),
    eligibility: t('config.editAgentic.tabs.eligibility'),
  };

  const knownPaths = new Set([
    'definition.persona.name',
    'definition.persona.voice_summary',
    'definition.persona.disclosure_rule',
    'cadence_instructions',
    ...doc.boundaries.rules.map((_, i) => `boundaries.rules.${i}.text`),
    ...doc.facts.flatMap((fact) => [
      `facts.${fact.key}.label`,
      `facts.${fact.key}.question_guidance`,
      `facts.${fact.key}.pattern`,
      `facts.${fact.key}.values`,
    ]),
    'scripts',
    'tools',
    ...doc.scripts.flatMap((script) => [
      `scripts[${script.product_script_key}].product_code`,
      `scripts[${script.product_script_key}].steps`,
      ...script.steps.flatMap((step) =>
        [
          'step_id',
          'goal',
          'exit_when',
          'required_facts',
          'allowed_tools',
          'on_decline',
          'on_ineligible',
          'on_consent_not_classified',
          'retry_guidance',
        ].map((field) => `scripts[${script.product_script_key}].steps[${step.step_id}].${field}`),
      ),
    ]),
    ...(doc.eligibility != null
      ? [
          'eligibility',
          'eligibility.age_limit_years',
          'eligibility.weight_limit_tonnes',
          'eligibility.questions',
          ...Object.keys(doc.eligibility.questions).map(
            (key) => `eligibility.questions[${key}].prompt`,
          ),
        ]
      : []),
    ...(doc.document_checklist != null
      ? [
          'document_checklist',
          'document_checklist.types',
          ...doc.document_checklist.sequence.map((key) => `document_checklist.sequence[${key}]`),
          ...doc.document_checklist.sequence.map(
            (key) => `document_checklist.request_templates[${key}]`,
          ),
        ]
      : []),
  ]);
  const unmatchedErrors = fieldErrors.filter((error) => !knownPaths.has(error.field_path));

  return (
    <div className="flex flex-col gap-4">
      {isLegacy ? (
        <div
          role="note"
          data-testid="agentic-legacy-notice"
          className="rounded-lg bg-surface-2 p-3 text-meta text-ink-1 ring-1 ring-inset ring-outline"
        >
          {t('config.editAgentic.legacyNotice')}
        </div>
      ) : null}

      {/* Section rail: a column from md up, a horizontally scrolling row below
          (CSS only). Tabs are 44px tall for touch (phone acceptance criteria). */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
        <div
          role="tablist"
          aria-label={t('config.editAgentic.tabsLabel')}
          className="flex gap-1 overflow-x-auto border-b border-outline md:sticky md:top-4 md:w-52 md:flex-shrink-0 md:flex-col md:overflow-visible md:border-b-0 md:border-r md:pr-3"
        >
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`agentic-tab-${tab}`}
              aria-selected={activeTab === tab}
              aria-controls="agentic-tabpanel"
              tabIndex={activeTab === tab ? 0 : -1}
              data-testid={`agentic-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              onKeyDown={onTabListKeyDown}
              className={`min-h-11 flex-shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-left text-body font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus md:whitespace-normal ${
                activeTab === tab
                  ? 'bg-selected text-ink-1'
                  : 'text-ink-2 hover:bg-surface-2 hover:text-ink-1'
              }`}
            >
              {tabLabel[tab]}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id="agentic-tabpanel"
          aria-labelledby={`agentic-tab-${activeTab}`}
          className="min-w-0 flex-1"
        >
          {activeTab === 'persona' ? (
            <PersonaTab
              persona={doc.definition.persona}
              onChange={setPersona}
              disabled={createDisabled}
              fieldErrors={fieldErrors}
            />
          ) : null}
          {activeTab === 'boundaries' ? (
            <BoundariesTab
              rules={doc.boundaries.rules}
              onChange={setBoundaryRules}
              disabled={createDisabled}
              fieldErrors={fieldErrors}
            />
          ) : null}
          {activeTab === 'cadence' ? (
            <CadenceTab
              cadenceInstructions={doc.cadence_instructions}
              onChange={setCadence}
              disabled={createDisabled}
              fieldErrors={fieldErrors}
            />
          ) : null}
          {activeTab === 'scripts' ? (
            <ScriptsTab
              apiClient={apiClient}
              scripts={doc.scripts}
              onChange={setScripts}
              facts={doc.facts}
              tools={doc.tools}
              disabled={createDisabled}
              fieldErrors={fieldErrors}
            />
          ) : null}
          {activeTab === 'facts' ? (
            <FactsTab
              facts={doc.facts}
              onChange={setFacts}
              disabled={createDisabled}
              fieldErrors={fieldErrors}
            />
          ) : null}
          {activeTab === 'eligibility' ? (
            <EligibilityChecklistPanel
              graphConfigJson={detail.graph_config_json}
              eligibility={doc.eligibility}
              documentChecklist={doc.document_checklist}
              onChangeEligibility={setEligibility}
              onChangeChecklist={setChecklist}
              disabled={createDisabled}
              fieldErrors={fieldErrors}
            />
          ) : null}
        </div>
      </div>

      {unmatchedErrors.length > 0 ? (
        <div
          role="alert"
          data-testid="agentic-unmatched-errors"
          className="min-w-0 break-words rounded-lg bg-status-down/15 p-3 text-meta text-status-down ring-1 ring-inset ring-status-down/40 [overflow-wrap:anywhere]"
        >
          <p className="font-medium">{t('config.edit.errors.otherFields')}</p>
          <ul className="list-inside list-disc">
            {unmatchedErrors.map((error) => (
              <li key={error.field_path}>
                {error.field_path}: {error.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {created != null ? (
        <ActivateVersionControl
          apiClient={apiClient}
          skillName={base.skillName}
          configVersion={created.config_version}
          productCode={base.productCode}
          isActive={false}
          onActivated={onActivated}
        />
      ) : null}

      {/* Sticky save bar: Save stays reachable while editing a long tab. */}
      <div className="sticky bottom-0 z-10 -mx-1 flex flex-col gap-1 border-t border-outline bg-surface-1 px-1 py-3 sm:flex-row sm:items-center sm:gap-4">
        <Button
          variant="primary"
          data-testid="agentic-create-submit"
          disabled={createDisabled || newConfigVersion.trim() === ''}
          aria-busy={createBusy}
          onClick={() => floating(handleCreate())}
          className="min-h-11 w-fit"
        >
          {createBusy ? t('config.edit.creating') : t('config.edit.create')}
        </Button>
        <p className="text-meta text-ink-2">{t('config.edit.saveOnlyHint')}</p>
      </div>
    </div>
  );
};
